"use client";

import React from "react";
import { Flower2, HeartHandshake, Stethoscope, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "匠心工藝",
  subTitle: "CRAFTSMANSHIP",
  description: "傳承數十年的木作經驗，將每一塊地板視為藝術品。",
  icon: Flower2
}, {
  title: "環保永續",
  subTitle: "SUSTAINABILITY",
  description: "嚴選國際認證板材，確保低甲醛排放，打造無毒空間。",
  icon: Stethoscope
}, {
  title: "在地服務",
  subTitle: "LOCAL SERVICE",
  description: "深耕台灣市場，提供專業到府測量與完善售後保固。",
  icon: HeartHandshake
}];
export default function NotJustGrooming() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section ref={sectionRef} data-section-id="176350" id="品牌故事-our-story" className="relative overflow-hidden bg-background py-24 lg:py-32 w-full">
  {/* 背景裝飾 */}
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute -left-[5%] top-0 h-[500px] w-[500px] rounded-full bg-primary/5 blur-[120px]" />
    <span className="absolute right-[-5%] top-1/4 select-none font-serif text-[18rem] font-bold leading-none text-foreground/[0.03]">
      Hent'
    </span>
  </div>

  {/*  核心改動：最穩固的置中母層 
      w-full (撐滿全螢幕) + flex flex-col items-center (強迫內部所有元件水平置中)
   */}
  <div className="w-full flex flex-col items-center justify-center">
    
    {/* 你的主容器：維持原本設定即可，在 flex items-center 的加持下，mx-auto 會發揮 100% 的置中威力 */}
    <div className="container relative z-10 mx-auto px-6">
      <div className="grid grid-cols-1 items-stretch gap-16 lg:grid-cols-12 lg:gap-24">

        {/* 左側：大圖區域 (佔 4 格 / col-4) */}
        <div className={cn("relative transition-all duration-1000 ease-out z-20 flex lg:col-span-4", isVisible ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0")}>
          <div className="relative z-20 aspect-[4/3.5] lg:aspect-auto w-full h-full overflow-hidden rounded-sm shadow-2xl">
            <img src="https://hent.services/wp-content/uploads/2026/05/About_Hent_D5081.webp" alt="Hent's 亨特地板" className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110" />
            <div className="absolute inset-0 bg-primary/5 mix-blend-multiply" />
          </div>
        </div>

        {/* 右側：文字內容與底部圖片區域 (佔 8 格 / col-8) */}
        <div className={cn("w-full flex-1 lg:pl-6 flex flex-col justify-between transition-all duration-[1.2s] ease-out lg:col-span-8 gap-y-8 lg:gap-y-10", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>

          {/* 上方文字群組 */}
          <div className="flex flex-col w-full">
            <div className="flex flex-col items-start w-fit">
              <div className="relative mb-4 w-full">
                <div className="flex items-center justify-between w-full mb-4">
                  <div className="h-[1px] bg-border w-53" /> 
                  <span className="text-primary/40 font-sans tracking-[0.3em] text-[10px] uppercase whitespace-nowrap pl-7">
                    ABOUT Hent's
                  </span>
                </div>
             
             {/*  核心改動：將 h2 變成 w-fit (寬度剛好跟字一樣長) 並且當作 relative 母層 */}
  <h2 className="relative w-fit text-2xl md:text-3xl font-hk-regular text-foreground tracking-tight leading-tight">
    
    {/*  核心改動：把奶茶色線條搬進 h2 裡面！
        這樣它的 left-0 bottom-1 w-full 就會「100% 精準對齊文字寬度」
        不管你上面 span 怎麼改、pl 怎麼加，底線永遠跟著中文標題的長度走！
     */}
    <div className="absolute left-0 bottom-1 w-full h-2 bg-[#D9C5B2]/80 -z-10" />
    
    理想的生活空間｜<br />
    應該從腳下開始被感受
  </h2>
              </div>
            </div>

            {/* 第三層概念：品牌主視覺核心文案 */}
            <div className="space-y-6 max-w-[650px]">
              <p className="text-foreground/70 text-lg leading-relaxed tracking-wide font-hk-light">
                Hent's 亨特超耐磨木地板提供完整的地板規劃與施工服務。<br className="hidden md:block" />
                從產品選材、空間搭配到施工細節，<br className="hidden md:block" />
                每一道流程皆由團隊細心把關。
              </p>
              <p className="text-foreground/70 text-lg leading-relaxed tracking-wide font-hk-light">
                我們希望帶給您的，不只是好看的地板，更是長久使用的安心感 ——
              </p>
            </div>
          </div>

        {/* 右側：底部圖片 */}
<div className="relative w-full h-48 lg:h-[220px] overflow-hidden rounded-sm shadow-lg group/bg z-30 lg:-ml-[144px] lg:w-[calc(100%+144px)] max-w-none">
  <div className="absolute inset-0 z-0">
    <img src="https://hent.services/wp-content/uploads/2026/05/About-US-L-scaled.jpg" alt="Hent's Flooring Background" className="w-full h-full object-cover object-center transition-transform duration-1000 group-hover/bg:scale-105" />

    <div className="absolute inset-0 backdrop-blur-[0.5px]" />
  </div>
  
  <div className="relative z-10 h-full p-6 lg:py-4 lg:pr-10 lg:pl-[144px] flex items-center">
    <Button asChild size="lg" className="h-12 rounded-none px-8 text-sm tracking-widest transition-all hover:gap-6 bg-[#604f45] text-white hover:bg-[#D9C5B2] hover:text-[#3d3d3d] shadow-md border-none font-hk-regular">
      <Link to="/ProjectsServicesPage" className="flex items-center gap-3">
        逛逛空間案例
        <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  </div>
</div>

        </div>

      </div>
    </div>

  </div>
</section>;
}